import { useCallback } from 'react';
import { logger } from '../../../../utils/logger';
import {
  useAddTaskColumn,
  useUpdateTaskColumn,
  useDeleteTaskColumn,
  useReorderTaskColumn,
} from '../../../../hooks/queries/useTaskColumns';
import { TaskColumn } from '../../../../types/topic';

interface UseColumnActionsProps {
  topicId: string;
  columns: TaskColumn[];
  setIsEditingColumnName: (columnId: string | null) => void;
  setColumnToDelete: (columnId: string | null) => void;
}

/**
 * Hook for column-related actions (add, update, delete, reorder)
 */
export function useColumnActions({
  topicId,
  columns,
  setIsEditingColumnName,
  setColumnToDelete,
}: UseColumnActionsProps) {
  // Get React Query mutation hooks
  const addColumnMutation = useAddTaskColumn();
  const updateColumnMutation = useUpdateTaskColumn();
  const deleteColumnMutation = useDeleteTaskColumn();
  const reorderColumnMutation = useReorderTaskColumn();

  // Add a new column after the specified column
  const handleAddColumnAfter = useCallback(
    async (afterColumnId: string) => {
      try {
        // Add the column using React Query mutation
        await addColumnMutation.mutateAsync({
          topicId,
          afterColumnId,
        });

        // The React Query hooks will automatically update the cache and UI
      } catch (error) {
        logger.error('Failed to add column', error);
      }
    },
    [addColumnMutation, topicId]
  );

  // Update a column's name
  const handleEditColumnName = useCallback(
    async (columnId: string, name: string) => {
      try {
        // First exit edit mode immediately to prevent flicker during the update
        setIsEditingColumnName(null);

        // Update column using React Query mutation
        // The optimistic UI update is handled by the mutation
        await updateColumnMutation.mutateAsync({
          columnId,
          updates: { name },
        });
      } catch (error) {
        logger.error('Failed to update column name', error);
      }
    },
    [updateColumnMutation, setIsEditingColumnName]
  );

  // Delete a column
  const handleDeleteColumn = useCallback(
    async (columnId: string | null) => {
      if (!columnId) return;

      try {
        await deleteColumnMutation.mutateAsync(columnId);
        setColumnToDelete(null);
      } catch (error) {
        logger.error('Failed to delete column', error);
      }
    },
    [deleteColumnMutation, setColumnToDelete]
  );

  // Update a column's color
  const handleColumnColorChange = useCallback(
    async (columnId: string, color: string) => {
      try {
        await updateColumnMutation.mutateAsync({
          columnId,
          updates: { color },
        });
      } catch (error) {
        logger.error('Failed to update column color', error);
      }
    },
    [updateColumnMutation]
  );

  // Move a column left (decrease index by 1)
  const handleMoveColumnLeft = useCallback(
    async (columnId: string) => {
      const currentIndex = columns.findIndex(c => c.id === columnId);
      if (currentIndex <= 0) return; // Already at the leftmost position

      try {
        await reorderColumnMutation.mutateAsync({
          topicId,
          columnId,
          newIndex: currentIndex - 1,
        });
      } catch (error) {
        logger.error('Failed to move column left', error);
      }
    },
    [columns, reorderColumnMutation, topicId]
  );

  // Move a column right (increase index by 1)
  const handleMoveColumnRight = useCallback(
    async (columnId: string) => {
      const currentIndex = columns.findIndex(c => c.id === columnId);
      if (currentIndex < 0 || currentIndex >= columns.length - 1) return; // Already at the rightmost position

      try {
        await reorderColumnMutation.mutateAsync({
          topicId,
          columnId,
          newIndex: currentIndex + 1,
        });
      } catch (error) {
        logger.error('Failed to move column right', error);
      }
    },
    [columns, reorderColumnMutation, topicId]
  );

  return {
    handleAddColumnAfter,
    handleEditColumnName,
    handleDeleteColumn,
    handleColumnColorChange,
    handleMoveColumnLeft,
    handleMoveColumnRight,
  };
}
